import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import EmptyState from '@/components/feature/EmptyState';
import UserAvatar from '@/components/feature/UserAvatar';
import CategoryBadge from '@/components/feature/CategoryBadge';
import { getAllUsers, getTopicById, getUserById, getUserReplies, getUserTopics } from '@/lib/community';
import { usePageMeta } from '@/hooks/usePageMeta';
import { cn, formatCount, formatDate, formatRelativeTime, excerpt } from '@/lib/format';

export default function UserPage() {
  const { userId = '' } = useParams();
  const user = getUserById(userId);
  const [tab, setTab] = useState<'topics' | 'replies'>('topics');

  usePageMeta({
    title: user ? `${user.name} 的个人主页 - 蜜糖社区` : '用户未找到 - 蜜糖社区',
    description: user
      ? `${user.name} 在蜜糖社区的个人主页：${user.bio} 查看TA发布的话题与最新回复。`
      : '该用户不存在，返回蜜糖社区发现更多有趣的创作者。',
    keywords: user ? `${user.name},蜜糖社区,用户主页,${user.badges.join(',')}` : '蜜糖社区,用户主页',
  });

  if (!user) {
    return (
      <Layout>
        <div className="w-full max-w-page mx-auto px-4 md:px-6 lg:px-8 py-16">
          <EmptyState icon="ri-user-forbid-line" title="没有找到这位用户" description="TA可能已经离开了社区，去首页看看其他活跃的分享者吧。" />
          <div className="mt-6 flex justify-center">
            <Link to="/" className="rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-bold px-5 py-2.5 whitespace-nowrap transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const userTopics = getUserTopics(user.id);
  const userReplies = getUserReplies(user.id);
  const suggestions = getAllUsers().filter((u) => u.id !== user.id).slice(0, 4);

  const stats = [
    { label: '发布话题', value: formatCount(user.postCount) },
    { label: '回复数', value: formatCount(user.replyCount) },
    { label: '关注', value: formatCount(user.following) },
    { label: '粉丝', value: formatCount(user.followers) },
  ];

  return (
    <Layout>
      <div className="w-full max-w-page mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-10">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: `${user.name} 的主页` }]} />

        {/* Profile header */}
        <div className="mt-5 relative overflow-hidden rounded-xl border border-background-200 bg-background-50">
          <div className="h-28 md:h-36 bg-primary-500" />
          <div className="px-5 md:px-8 pb-6 md:pb-7">
            <div className="-mt-12 md:-mt-14 flex items-end justify-between gap-4 flex-wrap">
              <UserAvatar src={user.avatar} alt={user.name} size="xl" ring className="border-4 border-background-50" />
              <div className="flex items-center gap-2 pb-1">
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-bold px-4 py-2 whitespace-nowrap transition-colors cursor-pointer"
                >
                  <i className="ri-user-add-line" />
                  关注
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-md border border-background-300 text-foreground-700 text-sm font-label font-bold px-4 py-2 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-message-3-line" />
                  私信
                </button>
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-xl md:text-2xl font-extrabold text-foreground-900">{user.name}</h1>
                {user.badges.map((badge) => (
                  <span key={badge} className="rounded-full bg-primary-100 text-primary-800 px-2.5 py-1 text-xs font-label font-bold">
                    {badge}
                  </span>
                ))}
              </div>
              <p className="mt-2.5 text-sm text-foreground-600 leading-relaxed max-w-2xl">{user.bio}</p>
              <p className="mt-2 text-xs text-foreground-400 inline-flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {formatDate(user.joinDate)} 加入蜜糖社区
              </p>
            </div>

            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {stats.map((stat) => (
                <div key={stat.label} className="rounded-lg bg-background-100 border border-background-200 px-4 py-3">
                  <p className="text-lg font-extrabold text-foreground-900">{stat.value}</p>
                  <p className="text-xs text-foreground-400 mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          <div className="lg:col-span-2">
            {/* Tabs */}
            <div className="inline-flex items-center gap-1 rounded-full bg-secondary-100 p-1 mb-5">
              <button
                type="button"
                onClick={() => setTab('topics')}
                className={cn(
                  'rounded-full px-4 py-1.5 text-sm font-label font-bold transition-colors cursor-pointer whitespace-nowrap',
                  tab === 'topics' ? 'bg-background-50 text-primary-700' : 'text-secondary-700 hover:text-secondary-900',
                )}
              >
                发表的话题 ({userTopics.length})
              </button>
              <button
                type="button"
                onClick={() => setTab('replies')}
                className={cn(
                  'rounded-full px-4 py-1.5 text-sm font-label font-bold transition-colors cursor-pointer whitespace-nowrap',
                  tab === 'replies' ? 'bg-background-50 text-primary-700' : 'text-secondary-700 hover:text-secondary-900',
                )}
              >
                最新回复 ({userReplies.length})
              </button>
            </div>

            {tab === 'topics' ? (
              userTopics.length > 0 ? (
                <div className="flex flex-col gap-3">
                  {userTopics.map((topic) => (
                    <article key={topic.id} className="flex gap-4 p-4 md:p-5 bg-background-50 border border-background-200 rounded-lg hover:border-primary-300 transition-colors">
                      <div className="flex-1 min-w-0">
                        <CategoryBadge name={topic.category.name} color={topic.category.color} icon={topic.category.icon} />
                        <Link to={`/topic/${topic.id}`} className="block mt-2 cursor-pointer">
                          <h3 className="text-base font-bold text-foreground-900 leading-snug hover:text-primary-700 transition-colors">{topic.title}</h3>
                        </Link>
                        <p className="mt-1.5 text-sm text-foreground-500 line-clamp-1">{excerpt(topic.summary, 80)}</p>
                        <div className="mt-2.5 flex items-center gap-4 text-xs text-foreground-400">
                          <span className="inline-flex items-center gap-1"><i className="ri-chat-3-line" />{formatCount(topic.replyCount)} 回复</span>
                          <span className="inline-flex items-center gap-1"><i className="ri-eye-line" />{formatCount(topic.viewCount)}</span>
                          <span className="inline-flex items-center gap-1"><i className="ri-time-line" />{formatRelativeTime(topic.createdAt)}</span>
                        </div>
                      </div>
                      <span className="hidden sm:block w-24 h-20 rounded-md overflow-hidden bg-background-200 shrink-0">
                        <img src={topic.cover} alt={topic.title} title={`${topic.title} - 蜜糖社区`} className="w-full h-full object-top" />
                      </span>
                    </article>
                  ))}
                </div>
              ) : (
                <EmptyState icon="ri-draft-line" title="TA 还没有发布过话题" description="关注一下，等TA分享第一篇文章。" />
              )
            ) : userReplies.length > 0 ? (
              <div className="flex flex-col gap-3">
                {userReplies.map((reply) => {
                  const topic = getTopicById(reply.topicId);
                  if (!topic) return null;
                  return (
                    <article key={reply.id} className="p-4 md:p-5 bg-background-50 border border-background-200 rounded-lg">
                      <p className="text-sm text-foreground-700 leading-relaxed">{reply.content}</p>
                      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap pt-3 border-t border-background-200">
                        <Link to={`/topic/${topic.id}`} className="text-xs text-foreground-500 hover:text-primary-700 transition-colors cursor-pointer inline-flex items-center gap-1.5 min-w-0">
                          <i className="ri-links-line shrink-0" />
                          <span className="truncate max-w-[240px]">{topic.title}</span>
                        </Link>
                        <span className="text-xs text-foreground-400">{formatRelativeTime(reply.createdAt)}</span>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <EmptyState icon="ri-chat-history-line" title="TA 还没有回复过话题" description="安静的观察者，也许某天就会开口。" />
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1 flex flex-col gap-5">
            <section className="bg-background-50 border border-background-200 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-user-star-line text-primary-600" />
                <h2 className="text-base font-extrabold text-foreground-900">你可能感兴趣的人</h2>
              </div>
              <div className="flex flex-col gap-1">
                {suggestions.map((s) => (
                  <Link key={s.id} to={`/user/${s.id}`} className="flex items-center gap-3 p-2 rounded-md hover:bg-background-100 transition-colors cursor-pointer">
                    <UserAvatar src={s.avatar} alt={s.name} size="sm" />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-bold text-foreground-900 truncate">{s.name}</span>
                      <span className="block text-[11px] text-foreground-400 truncate">{s.badges[0]}</span>
                    </span>
                    <span className="text-[11px] text-foreground-400 shrink-0">{formatCount(s.followers)} 粉丝</span>
                  </Link>
                ))}
              </div>
            </section>

            <section className="bg-primary-100 border border-primary-200 rounded-lg p-5">
              <h2 className="text-base font-extrabold text-foreground-900">也想展示自己？</h2>
              <p className="mt-2 text-sm text-foreground-700 leading-relaxed">发布一个话题，让更多人认识你的兴趣和观点。</p>
              <Link to="/post" className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-bold px-4 py-2 whitespace-nowrap transition-colors cursor-pointer">
                <i className="ri-add-line" />
                去发布
              </Link>
            </section>
          </aside>
        </div>
      </div>
    </Layout>
  );
}